import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { CircleCheck, MoreHorizontal, Trash2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AlertModal } from '@/components/modal/alert-modal';
import { useState } from 'react';
import { useDeleteOrganisationMediasMutation } from '@/services/organisations/queries';
import { showToast } from '@/components/ui/show-toast';

interface CellActionProps {
  data: any;
  organisationId?: string;
}

export const CellAction: React.FC<CellActionProps> = ({ data, organisationId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const link = data?.url?.trim()
    ? data.url
    : data?.file?.url?.trim()
      ? data.file.url
      : null;

  const { mutate: deleteMedia, isPending: isLoading } = useDeleteOrganisationMediasMutation(
    organisationId || '',
    () => {
      showToast('success', 'Media file deleted successfully');
      setIsOpen(false);
    },
    () => {
      showToast('error', 'Could not delete Media file. Please try again.');
    }
  );

  async function onConfirm() {
    if (!organisationId) {
      showToast('error', 'Organization data not available');
      return;
    }

    // Get the media URL for deletion
    const mediaUrl = data?.url || data?.file?.url;
    if (!mediaUrl) {
      showToast('error', 'Media URL not found');
      return;
    }

    // Delete the media using the URL
    deleteMedia([mediaUrl]);
  }

  return (
    <>
      <AlertModal
        title='Are you sure you want to delete this media'
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={onConfirm}
        loading={isLoading}
      />
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='h-8 w-8 p-0'>
            <span className='sr-only'>Open menu</span>
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuLabel>Actions</DropdownMenuLabel>

          <DropdownMenuItem>
            {!link && (
              <span className='italic text-muted-foreground'>No file</span>
            )}
            {link && (
              <Link
                to={link}
                target='_blank'
                className='flex items-center gap-1'
              >
                <CircleCheck className='mr-2 h-4 w-4' /> Open link in new tab
              </Link>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem>
            <div
              onClick={() => setIsOpen(true)}
              className='flex items-center gap-1'
            >
              <Trash2Icon className='mr-2 h-4 w-4' color='red' /> Delete media
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
